import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'

import { Provider } from '../../../providers/ProvidersProvider/types'
import {
  ProviderTaskBarrier,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
  ProviderType,
  UpdateProviderTaskRequest,
} from '../../../providers/ProviderTask/ProviderTask.provider'

interface EditProviderTaskModalProps {
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  showEditModal: boolean
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
  selectedTask: ProviderTaskResponse | null
  taskTypes: ProviderTaskType[]

  formProviderTypeId: number | null
  setFormProviderTypeId: React.Dispatch<React.SetStateAction<number | null>>
  providerTypes: ProviderType[]

  providers: Provider[]
  priorityLevels: ProviderTaskPriorityLevel[]
  statuses: ProviderTaskStatus[]
  barriers: ProviderTaskBarrier[]

  formTaskTypeId: number | ''
  setFormTaskTypeId: React.Dispatch<React.SetStateAction<number>>

  formAssignedToProviderId: number | null
  setFormAssignedToProviderId: React.Dispatch<
    React.SetStateAction<number | null>
  >

  formPriorityLevelId: number | ''
  setFormPriorityLevelId: React.Dispatch<React.SetStateAction<number>>

  formStatusId: number | ''
  setFormStatusId: React.Dispatch<React.SetStateAction<number>>

  formBarrierId: number | null
  setFormBarrierId: React.Dispatch<React.SetStateAction<number | null>>

  formDueAt: Dayjs | null
  setFormDueAt: React.Dispatch<React.SetStateAction<Dayjs | null>>
  onUpdateTask: (request: UpdateProviderTaskRequest) => Promise<any>
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  >
}

export const EditProviderTaskModal = ({
  loading,
  setLoading,
  showEditModal,
  setShowEditModal,
  selectedTask,
  taskTypes,
  formProviderTypeId,
  setFormProviderTypeId,
  providerTypes,
  providers,
  priorityLevels,
  statuses,
  barriers,
  formTaskTypeId,
  setFormTaskTypeId,
  formAssignedToProviderId,
  setFormAssignedToProviderId,
  formPriorityLevelId,
  setFormPriorityLevelId,
  formStatusId,
  setFormStatusId,
  formBarrierId,
  setFormBarrierId,
  formDueAt,
  setFormDueAt,
  onUpdateTask,
  setOptimisticTasks,
}: EditProviderTaskModalProps) => {
  const handleUpdateTask = async () => {
    setLoading(true)

    const requiredFields = [
      { value: formTaskTypeId, label: 'task type' },
      { value: formPriorityLevelId, label: 'priority level' },
      { value: formStatusId, label: 'status' },
      { value: formDueAt, label: 'due date' },
    ]

    const missingFields = requiredFields
      .filter((field) => !field.value)
      .map((field) => field.label)

    if (missingFields.length > 0) {
      let message = ''

      if (missingFields.length === 1) {
        message = `Please select a ${missingFields[0]}.`
      } else if (missingFields.length === 2) {
        message = `Please select a ${missingFields[0]} and ${missingFields[1]}.`
      } else {
        const lastField = missingFields.pop()

        message = `Please select a ${missingFields.join(
          ', '
        )}, and ${lastField}.`
      }

      alert(message)
      setLoading(false)
      return
    }

    const hasProvider = !!formAssignedToProviderId
    const hasProviderType = !!formProviderTypeId

    // They can only be equivalent if both are empty
    if (hasProvider === hasProviderType) {
      alert('Please select either a provider or a provider type.')
      setLoading(false)
      return
    }

    if (
      selectedTask &&
      formTaskTypeId &&
      formPriorityLevelId &&
      formStatusId &&
      formDueAt
    ) {
      const taskType = taskTypes.find((t) => t.id === formTaskTypeId)

      const assignedProviders = formAssignedToProviderId
        ? providers
            .filter((p) => p.providerId === formAssignedToProviderId)
            .map((p) => ({
              providerId: p.providerId,
              providerName: p.name,
            }))
        : providers
            .filter((p) => p.providerTypeId === formProviderTypeId)
            .map((p) => ({
              providerId: p.providerId,
              providerName: p.name,
            }))

      if (!assignedProviders.length) {
        alert('No providers found for this task.')
        setLoading(false)
        return
      }

      const priorityLevel = priorityLevels.find(
        (p) => p.id === formPriorityLevelId
      )
      const status = statuses.find((s) => s.id === formStatusId)
      const barrier = formBarrierId
        ? barriers.find((b) => b.id === formBarrierId) ?? null
        : null

      if (!taskType || !priorityLevel || !status) {
        alert('TaskType or PriorityLevel or Status not found.')
        setLoading(false)
        return
      }

      const previousTask = selectedTask // snapshot for rollback

      const optimisticTask: ProviderTaskResponse = {
        ...selectedTask,
        providerTaskType: taskType,

        assignedProviders,

        priorityLevel,
        status,
        barrier,
        dueAt: formDueAt.toISOString(),
      }

      // Update immediately, close modal right away
      setOptimisticTasks((prev) =>
        prev.map((t) => (t.id === selectedTask.id ? optimisticTask : t))
      )

      try {
        const updatedTask = await onUpdateTask({
          providerTaskId: selectedTask.id,
          providerTaskTypeId: formTaskTypeId as number,
          providerTypeId: formProviderTypeId,
          assignedToProviderId: formAssignedToProviderId as number,
          providerTaskPriorityLevelId: formPriorityLevelId as number,
          providerTaskStatusId: formStatusId as number,
          providerTaskBarrierId: formBarrierId,
          dueAt: formDueAt.toISOString(),
        })

        setOptimisticTasks((prev) =>
          prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
        )

        setShowEditModal(false)
      } catch (error) {
        console.log('onUpdateTask-error', error)
        // Rollback to the pre-edit task
        setOptimisticTasks((prev) =>
          prev.map((t) => (t.id === selectedTask.id ? previousTask : t))
        )
        setShowEditModal(true) // reopen so they can retry without losing form state
        alert('Failed to update task. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    setLoading(false)
  }

  return (
    <Dialog
      open={showEditModal}
      onClose={() => setShowEditModal(false)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle fontSize={18}>Edit Task</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <FormControl size='small' fullWidth sx={{ mt: 1 }}>
          <InputLabel>Task Type</InputLabel>
          <Select
            value={formTaskTypeId}
            label='Task Type'
            onChange={(e) => setFormTaskTypeId(e.target.value as number)}
          >
            {taskTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size='small'
          fullWidth
          options={providerTypes}
          disabled={!!formAssignedToProviderId}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            setFormProviderTypeId(value?.id ?? null)

            // Clear specific provider when selecting a provider type
            if (value) {
              setFormAssignedToProviderId(null)
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={`provider-type-${option.id}`}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                formAssignedToProviderId
                  ? 'Unable to select both provider type and provider'
                  : 'Provider Type (Optional)'
              }
            />
          )}
        />

        <Autocomplete
          size='medium'
          sx={{ marginY: 1, fontSize: 16 }}
          fullWidth
          options={providers}
          disabled={!!formProviderTypeId}
          getOptionLabel={(option) => `${option.name}`}
          value={
            providers.find((p) => p.providerId === formAssignedToProviderId) ??
            null
          }
          isOptionEqualToValue={(option, value) =>
            option.providerId === value.providerId
          }
          onChange={(_, value) => {
            setFormAssignedToProviderId(value?.providerId ?? 0)

            // just in case, clear provider type when selecting a specific provider
            if (value) {
              setFormProviderTypeId(null)
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.providerId}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                formProviderTypeId
                  ? 'Unable to select both provider type and provider'
                  : 'Assign to Provider'
              }
            />
          )}
        />

        <FormControl size='small' fullWidth>
          <InputLabel>Priority Level</InputLabel>
          <Select
            value={formPriorityLevelId}
            label='Priority Level'
            onChange={(e) => setFormPriorityLevelId(e.target.value as number)}
          >
            {priorityLevels.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formStatusId}
            label='Status'
            onChange={(e) => setFormStatusId(e.target.value as number)}
          >
            {statuses.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Barrier (Optional)</InputLabel>
          <Select
            value={formBarrierId ?? ''}
            label='Barrier (Optional)'
            onChange={(e) =>
              setFormBarrierId(
                e.target.value ? (e.target.value as number) : null
              )
            }
          >
            <MenuItem value=''>None</MenuItem>
            {barriers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.barrier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label='Due Date'
          value={formDueAt}
          onChange={(date) => setFormDueAt(date)}
          renderInput={(params: any) => (
            <TextField {...params} size='small' fullWidth />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setShowEditModal(false)} sx={{ fontSize: 15 }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleUpdateTask}
          sx={{ fontSize: 15 }}
        >
          {loading ? <CircularProgress size={20} color='secondary' /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
